/**
 * Bulk Add to Cart - Table View Selection
 * Allows users to select multiple products in table view and add them all to cart at once
 */

class BulkAddToCart {
  constructor() {
    this.selectedProducts = new Map(); // Use Map instead of Set for proper tracking
    this.bulkActionsBar = document.getElementById('bulk-actions-bar');
    this.selectAllCheckbox = document.getElementById('select-all-products');
    this.productCheckboxes = [];
    this.selectedCountElement = document.getElementById('selected-count-number');
    this.bulkAddButton = document.getElementById('btn-bulk-add-to-cart');
    this.clearButton = document.getElementById('btn-clear-selection');
    this.isProcessing = false;

    this.init();
  }

  init() {
    if (!this.bulkActionsBar) {
      return;
    }

    // Initialize event listeners
    this.setupEventListeners();

    // Watch for view changes
    document.addEventListener('collectionViewChanged', (e) => {
      this.handleViewChange(e.detail.view);
    });

    // Initialize checkboxes
    this.refreshCheckboxes();
  }

  setupEventListeners() {
    // Use event delegation for checkboxes instead of binding to each one
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('product-select-checkbox')) {
        this.handleCheckboxChange(e);
      }

      if (e.target.id === 'select-all-products') {
        this.toggleSelectAll(e.target.checked);
      }
    });

    // Prevent checkbox clicks from triggering product link
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('product-select-checkbox')) {
        e.stopPropagation();
      }
    });

    // Bulk add button
    if (this.bulkAddButton) {
      this.bulkAddButton.addEventListener('click', () => {
        this.addSelectedToCart();
      });
    }

    // Clear selection button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.clearSelection();
      });
    }

    // Select All Visible button
    const selectAllVisibleBtn = document.getElementById('btn-select-all-visible');
    if (selectAllVisibleBtn) {
      selectAllVisibleBtn.addEventListener('click', () => {
        this.selectAllVisible();
      });
    }
  }

  selectAllVisible() {
    const checkboxes = document.querySelectorAll('.product-select-checkbox');
    checkboxes.forEach(checkbox => {
      const productCard = checkbox.closest('.collection-product-card');

      if (productCard && this.isElementVisible(productCard)) {
        if (!checkbox.checked) {
          checkbox.checked = true;
          // Trigger change event
          const event = new Event('change', { bubbles: true });
          checkbox.dispatchEvent(event);
        }
      }
    });
  }

  isElementVisible(element) {
    if (!element) return false;
    if (!document.body.contains(element)) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    return true;
  }

  refreshCheckboxes() {
    // We now use event delegation, so we just need to update the UI
    this.updateUI();
  }

  handleCheckboxChange = (e) => {
    const checkbox = e.target;
    const variantId = checkbox.dataset.variantId;
    const productId = checkbox.dataset.productId;
    const productTitle = checkbox.dataset.productTitle;

    if (checkbox.checked) {
      this.selectedProducts.set(productId, {
        variantId,
        productId,
        productTitle
      });
    } else {
      this.selectedProducts.delete(productId);
    }

    this.updateUI();
  }

  toggleSelectAll(checked) {
    const checkboxes = document.querySelectorAll('.product-select-checkbox');
    checkboxes.forEach(checkbox => {
      if (checkbox.checked !== checked) {
        checkbox.checked = checked;
        // Trigger change event to update selectedProducts
        const event = new Event('change', { bubbles: true });
        checkbox.dispatchEvent(event);
      }
    });
  }

  updateUI() {
    const count = this.selectedProducts.size;

    if (this.selectedCountElement) {
      this.selectedCountElement.textContent = count;
    }

    if (this.bulkActionsBar) {
      if (count > 0) {
        this.bulkActionsBar.classList.add('active');
      } else {
        this.bulkActionsBar.classList.remove('active');
      }
    }

    if (this.bulkAddButton) {
      this.bulkAddButton.disabled = count === 0 || this.isProcessing;
    }

    const selectAllCheckbox = document.getElementById('select-all-products');
    if (selectAllCheckbox) {
      const checkboxes = Array.from(document.querySelectorAll('.product-select-checkbox'));
      if (checkboxes.length > 0) {
        const checkedCount = checkboxes.filter(cb => cb.checked).length;
        selectAllCheckbox.checked = checkedCount === checkboxes.length;
        selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
      }
    }
  }

  async addSelectedToCart() {
    if (this.selectedProducts.size === 0 || this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.updateButtonState('loading');

    const products = Array.from(this.selectedProducts.values());
    const items = products.map(product => ({
      id: product.variantId,
      quantity: 1
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: items
        })
      });

      const data = await response.json();

      if (data.status && data.status === 422) {
        throw new Error(data.description || 'Unable to add items to cart');
      }

      this.updateButtonState('success');

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bulk-add-to-cart',
          itemCount: products.length
        });
      }

      if (typeof window.updateStickyCart === 'function') {
        window.updateStickyCart();
      }

      setTimeout(() => {
        this.clearSelection();
        this.updateButtonState('default');
        this.isProcessing = false;
      }, 1500);

    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.updateButtonState('error');
      alert(error.message || 'Error adding items to cart. Please try again.');

      setTimeout(() => {
        this.updateButtonState('default');
        this.isProcessing = false;
      }, 2000);
    }
  }

  updateButtonState(state) {
    if (!this.bulkAddButton) return;

    const buttonText = this.bulkAddButton.querySelector('svg').nextSibling || this.bulkAddButton.lastChild;

    switch (state) {
      case 'loading':
        this.bulkAddButton.disabled = true;
        buttonText.textContent = ' Adding to Cart...';
        this.bulkAddButton.style.opacity = '0.7';
        break;

      case 'success':
        this.bulkAddButton.disabled = true;
        buttonText.textContent = ' Added Successfully!';
        this.bulkAddButton.style.opacity = '1';
        break;

      case 'error':
        this.bulkAddButton.disabled = false;
        buttonText.textContent = ' Failed - Try Again';
        this.bulkAddButton.style.opacity = '1';
        break;

      case 'default':
      default:
        this.bulkAddButton.disabled = this.selectedProducts.size === 0;
        buttonText.textContent = ' Add Selected to Cart';
        this.bulkAddButton.style.opacity = '1';
        break;
    }
  }

  clearSelection() {
    const checkboxes = document.querySelectorAll('.product-select-checkbox');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    this.selectedProducts.clear();

    const selectAllCheckbox = document.getElementById('select-all-products');
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = false;
      selectAllCheckbox.indeterminate = false;
    }

    this.updateUI();
  }

  handleViewChange(view) {
    if (view !== 'table') {
      this.clearSelection();
    } else {
      setTimeout(() => {
        this.updateUI();
      }, 200);
    }
  }

  refresh() {
    this.updateUI();
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.bulkAddToCart = new BulkAddToCart();
  });
} else {
  window.bulkAddToCart = new BulkAddToCart();
}

// Handle dynamic content loading
document.addEventListener('collectionProductsLoaded', () => {
  if (window.bulkAddToCart) {
    window.bulkAddToCart.refresh();
  }
});

// Handle Shopify section reloads
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    if (window.bulkAddToCart) {
      window.bulkAddToCart.refresh();
    }
  }, 100);
});
